/**
 * Config and OIDC client for the shared `@cyngn/auth-react` provider (DEV-5751).
 *
 * The library's `OidcClient` owns Discovery, the authorization-code + PKCE redirect, the token
 * request, the refresh grant and the logout URL, so the app builds none of those itself.
 *
 * One cloud-core hop stays: `/login/cognito/` provisions the Django user behind an SSO login
 * (cloud-core's bearer auth resolves users but does not create them) and returns the session it
 * wants the SPA to hold, so the code exchange is followed by that call and its tokens win. The
 * refresh grant goes straight to the IdP (DEV-6053): cloud-core's `/login/cognito/refresh/` only
 * proxied it under the same app client, and the SPA holds the refresh token already.
 */
import {
  AuthConfigError,
  OidcClient,
  browserEnv,
  readAuthConfig,
  type AuthConfig,
  type EnvMapping,
  type TokenSet,
} from "@cyngn/auth-react";

import API from "./api";
import { bearerExpiresAtMs } from "./authTokens";

/**
 * A config a deployment without Cognito can still render with: `readAuthConfig` refuses to
 * guess a pool, and a deployment with no `COGNITO_*` in `window._env_` must still mount rather
 * than crash. An empty `clientId` is what makes the login page report SSO as unavailable.
 */
const UNCONFIGURED_AUTH_CONFIG: AuthConfig = {
  provider: "cognito",
  issuerUrl: "",
  discoveryUrl: "",
  clientId: "",
  scopes: ["openid", "email", "profile"],
  redirectUri: `${window.location.origin}/login/callback`,
  postLogoutRedirectUri: `${window.location.origin}/login`,
  hostedUiDomain: null,
  identityProvider: null,
  groupsClaim: "cognito:groups",
  companiesClaim: "custom:company_ids",
  adminConsoleEnabled: false,
};

/**
 * The deployment writes `window._env_` from `docker-entrypoint.sh`, which emits the app's own
 * `COGNITO_*` names. The library reads `AUTH_OIDC_*` and already falls back to `COGNITO_REGION`,
 * `COGNITO_USER_POOL_ID` and `COGNITO_DOMAIN`, but not to the client id — so the deployed names
 * are mapped here, with an explicitly configured `AUTH_OIDC_*` value always winning.
 */
const withLegacyCognitoNames = (env: EnvMapping): EnvMapping => ({
  ...env,
  AUTH_OIDC_CLIENT_ID: env.AUTH_OIDC_CLIENT_ID || env.COGNITO_CLIENT_ID,
});

export const readAppAuthConfig = (
  env: EnvMapping = browserEnv()
): AuthConfig => {
  try {
    // No `identity_provider` unless `AUTH_OIDC_IDENTITY_PROVIDER` names one: the hosted UI is
    // where a Cognito-native user types an email and password, and pinning Google skips it.
    return readAuthConfig(withLegacyCognitoNames(env));
  } catch (error) {
    if (error instanceof AuthConfigError) {
      console.warn("[auth] Cognito is not configured: %s", error.message);
      return UNCONFIGURED_AUTH_CONFIG;
    }
    throw error;
  }
};

interface CloudCoreTokenResponse {
  // cloud-core answers in camelCase (DRF CamelCase renderer).
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  expiresIn?: number;
}

/**
 * The session to hold after a cloud-core call, falling back to the tokens we already have for
 * anything the response leaves out.
 */
const toTokenSet = (
  data: CloudCoreTokenResponse | null | undefined,
  current: TokenSet
): TokenSet => {
  const response = data ?? {};
  const idToken = response.idToken ?? current.idToken;
  return {
    idToken,
    accessToken: response.accessToken ?? current.accessToken,
    refreshToken: response.refreshToken ?? current.refreshToken,
    // The refresh clock follows the token that is actually presented (the ID token).
    expiresAt: bearerExpiresAtMs(idToken, response.expiresIn),
  };
};

const secondsUntil = (expiresAt: number | null): number =>
  expiresAt === null
    ? 0
    : Math.max(0, Math.round((expiresAt - Date.now()) / 1000));

/**
 * The library's `OidcClient` with two app-specific hops layered on top. Composed by patching the
 * instance rather than subclassing: the app compiles to ES5, which cannot `extend` a native class
 * (the ES5 `_super.call(this)` helper throws "Class constructor cannot be invoked without 'new'").
 */
export const createCloudCoreOidcClient = (config: AuthConfig): OidcClient => {
  // Resolve `fetch` per call: the library binds `globalThis.fetch` at construction, which is
  // absent in jsdom and must not be captured by the process-wide client at import time.
  const client = new OidcClient(config, {
    fetchImpl: (input, init) => fetch(input, init),
  });
  const baseCompleteLogin = client.completeLogin.bind(client);
  const baseRefresh = client.refresh.bind(client);

  /** PKCE exchange with Cognito, then cloud-core provisioning, as the callback page did. */
  client.completeLogin = async (
    callbackUrl: string = window.location.href
  ): Promise<{ tokens: TokenSet; returnTo: string | null }> => {
    const { tokens, returnTo } = await baseCompleteLogin(callbackUrl);
    const { data } = await API.post<CloudCoreTokenResponse>("/login/cognito/", {
      id_token: tokens.idToken,
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
      expires_in: secondsUntil(tokens.expiresAt),
    });
    return { tokens: toTokenSet(data, tokens), returnTo };
  };

  /** The IdP refresh grant, clocked by the ID token that is actually presented. */
  client.refresh = async (refreshToken: string): Promise<TokenSet> => {
    const next = await baseRefresh(refreshToken);
    return {
      ...next,
      expiresAt: bearerExpiresAtMs(
        next.idToken,
        next.expiresAt === null
          ? null
          : Math.round((next.expiresAt - Date.now()) / 1000)
      ),
    };
  };

  return client;
};

export const appAuthConfig: AuthConfig = readAppAuthConfig();

export const appOidcClient = createCloudCoreOidcClient(appAuthConfig);

/** Whether SSO can be started at all; there is no other login. */
export const isCognitoConfigured = (): boolean =>
  !!appAuthConfig.clientId && !!appAuthConfig.hostedUiDomain;
